// 디렉토리 경로: client/src/components/reference-analysis/
// 파일명: ReferenceInsightRegisterPanel.tsx
// 파일 코드의 역할/설명: 분석 결과에서 구조/아이디어/주제를 기존 레퍼런스 글로 등록한다.

import { useCallback, useState } from 'react'
import type {
  InsightRegisterKind,
  ReferenceAnalysisResult,
  ReferenceItem,
} from '@/types'
import styles from './ReferenceAnalysisModal.module.css'

interface ReferenceInsightRegisterPanelProps {
  source: ReferenceItem
  result: ReferenceAnalysisResult
  isSaving: boolean
  onRegister: (kind: InsightRegisterKind, value: string) => Promise<void>
}

const REGISTER_GROUPS: Array<{
  kind: InsightRegisterKind
  title: string
  emptyText: string
}> = [
  {
    kind: 'structure',
    title: '구조 등록',
    emptyText: '추출된 구조가 없습니다.',
  },
  {
    kind: 'idea',
    title: '아이디어 등록',
    emptyText: '추출된 아이디어가 없습니다.',
  },
  {
    kind: 'topic',
    title: '주제 등록',
    emptyText: '추출된 주제가 없습니다.',
  },
]

function getValues(
  result: ReferenceAnalysisResult,
  kind: InsightRegisterKind
): string[] {
  if (kind === 'structure') return result.extractedStructures
  if (kind === 'idea') return result.extractedIdeas
  return result.extractedTopics
}

export function ReferenceInsightRegisterPanel({
  source,
  result,
  isSaving,
  onRegister,
}: ReferenceInsightRegisterPanelProps) {
  const [customKind, setCustomKind] =
    useState<InsightRegisterKind>('structure')
  const [customValue, setCustomValue] = useState('')
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const handleRegister = useCallback(
    async (kind: InsightRegisterKind, value: string) => {
      const trimmed = value.trim()
      if (!trimmed || isSaving) return

      const key = `${kind}:${trimmed}`
      setActiveKey(key)
      try {
        await onRegister(kind, trimmed)
      } finally {
        setActiveKey(null)
      }
    },
    [isSaving, onRegister]
  )

  const handleCustomRegister = useCallback(async () => {
    await handleRegister(customKind, customValue)
    setCustomValue('')
  }, [customKind, customValue, handleRegister])

  return (
    <section className={styles.registerPanel} aria-labelledby="register-title">
      <div className={styles.sectionHeader}>
        <h4 id="register-title">인사이트 등록</h4>
        <span className={styles.sourceBadge}>{source.title}</span>
      </div>

      <div className={styles.registerGrid}>
        {REGISTER_GROUPS.map((group) => {
          const values = getValues(result, group.kind)
          return (
            <div key={group.kind} className={styles.registerGroup}>
              <h5>{group.title}</h5>
              {values.length === 0 ? (
                <p className={styles.emptyInline}>{group.emptyText}</p>
              ) : (
                <div className={styles.choiceList}>
                  {values.map((value) => {
                    const key = `${group.kind}:${value}`
                    return (
                      <button
                        key={key}
                        type="button"
                        className={styles.choiceButton}
                        disabled={isSaving}
                        onClick={() => handleRegister(group.kind, value)}
                      >
                        <span>{value}</span>
                        <strong>
                          {activeKey === key ? '저장 중' : '등록'}
                        </strong>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className={styles.customRegister}>
        <select
          value={customKind}
          onChange={(event) =>
            setCustomKind(event.target.value as InsightRegisterKind)
          }
          disabled={isSaving}
        >
          <option value="structure">구조</option>
          <option value="idea">아이디어</option>
          <option value="topic">주제</option>
        </select>
        <input
          value={customValue}
          onChange={(event) => setCustomValue(event.target.value)}
          placeholder="직접 등록할 인사이트"
          disabled={isSaving}
        />
        <button
          type="button"
          onClick={handleCustomRegister}
          disabled={isSaving || !customValue.trim()}
        >
          직접 등록
        </button>
      </div>
    </section>
  )
}

export default ReferenceInsightRegisterPanel
